// ----------------------
// Type Definitions
// ----------------------

import { ROLES } from '../../constants';
import { getGroupbyId, getRealmUserInfo } from '../../services';

export interface StaffRelationPic {
  imageContent: string;
}

export interface StaffRelation {
  practiseAdminLivePic?: StaffRelationPic;
}

export interface StaffItem {
  practiseUserId: string;
  role: string;
  departmentId: string;
  practiseUserRelation: StaffRelation;
}

export interface OrganisationDetails {
  id: string;
  logoUrl?: string;
  practiseAdminDetails?: Array<{
    practiseUserId: string;
    departmentId: string;
    practiseUserRelation: StaffRelation;
  }>;
  physicianData?: StaffItem[];
  departmentAdminData?: StaffItem[];
  otherStaffData?: StaffItem[];
  doctorsCount?: { aggregate?: { count: number } };
  physicianCount?: { aggregate?: { count: number } };
  otherStaffCount?: { aggregate?: { count: number } };
}

export interface RealmUser {
  firstName: string;
  lastName: string;
}

export interface DropdownRole {
  label: string;
  value: string;
}

export interface MappedStaff {
  role: string;
  name: string;
  practiseUserId: string;
  departmentId: string;
  department: string;
  image?: string;
}

// ----------------------
// Role Resolution Helper
// ----------------------

export const resolveUserRole = (roles: string[] | undefined): string => {
  if (!roles) return '';

  if (roles.includes(ROLES.SUPER_ADMIN)) return ROLES.SUPER_ADMIN;
  if (roles.includes(ROLES.PRACTISE_ADMIN)) return ROLES.PRACTISE_ADMIN;
  if (roles.includes(ROLES.DEPT_ADMIN)) return ROLES.DEPT_ADMIN;
  if (roles.includes(ROLES.DOCTOR)) return ROLES.DOCTOR;
  if (roles.includes(ROLES.OTHER)) return ROLES.OTHER;

  return '';
};

// ----------------------
// Generate Image Helper
// ----------------------

export const getSignedImage = async (
  content: string | undefined,
  generateSignedUrl: (arg: string) => Promise<string>
): Promise<string | undefined> => {
  if (!content) return undefined;
  return generateSignedUrl(content);
};

// ----------------------
// Map Staff Data Helper
// ----------------------

export const mapStaffData = async (
  staffArray: StaffItem[] = [],
  userAccessToken: string,
  generateSignedUrl: (arg: string) => Promise<string>
): Promise<MappedStaff[]> => {
  return Promise.all(
    staffArray.map(async (item) => {
      const dept = await getGroupbyId(item.departmentId, userAccessToken);
      const user = await getRealmUserInfo(item.practiseUserId, userAccessToken);

      const departmentName = dept?.name ?? 'N/A';
      const fullName = `${user.firstName} ${user.lastName}`;

      const image = await getSignedImage(
        item.practiseUserRelation?.practiseAdminLivePic?.imageContent,
        generateSignedUrl
      );

      return {
        role: item.role,
        name: fullName,
        practiseUserId: item.practiseUserId,
        departmentId: item.departmentId,
        department: departmentName,
        image
      };
    })
  );
};
